import React, { useMemo, useState } from 'react';
import { merge, sumBy } from 'lodash';
import { FilerrUploader } from '@fiverr-private/filerr_uploader';
import { logger } from '@fiverr-private/obs';
import { Dropzone } from '@fiverr-private/dropzone';
import { translate } from '@fiverr-private/i18n-react';
import { useTemplatesData } from '../../hooks';
import { TemplateResponseObject } from '../../api';
import { ATTACHMENT_UPLOAD_CONFIG, UploadErrorCodes, UploadErrorCodesList } from './consts';
import {
    Attachment,
    AttachmentUploadProps,
    DropzoneOnProgressData,
    DropzoneOnSuccessData,
    FileUploadContextType,
} from './types';
import { generateAttachmentId } from './utils';

const ERROR_TRANSLATIONS_PREFIX = 'legal_documents_package.uploadErrors';

export const useAttachments = ({ initialAttachments = [] }: AttachmentUploadProps = {}): FileUploadContextType => {
    const [filerrUploader] = useState(() => new FilerrUploader(ATTACHMENT_UPLOAD_CONFIG));
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [rawAttachments, setRawAttachments] = useState<Attachment[]>(initialAttachments);
    const resetState = () => setRawAttachments([]);
    const templatesData = useTemplatesData();

    const exitingDocumentsNames = useMemo(() => {
        const documents: TemplateResponseObject[] = templatesData?.templatesQuery?.data || [];
        return documents
            .filter((doc: TemplateResponseObject) => doc.status !== 'deleted')
            .map((doc: TemplateResponseObject) => doc.fileName);
    }, [templatesData?.templatesQuery?.data]);

    const uploadProgress = useMemo(() => {
        const loadingFiles = rawAttachments.filter((attachment) => !attachment.uploaded && !attachment.errorMessage);
        const isLoading = !!loadingFiles.length;
        const progressPercent = sumBy(loadingFiles, (attachment) => attachment.progress || 0) / loadingFiles.length;
        return { isLoading, progressPercent };
    }, [rawAttachments]);

    const uploadedFiles = useMemo(
        () => rawAttachments.filter((attachment) => attachment.uploaded && !attachment.errorMessage),
        [rawAttachments]
    );

    const rawAttachmentsFilesNames = useMemo(() => {
        const documents: Attachment[] = rawAttachments || [];
        return documents.map((doc: Attachment) => doc.name || doc.file_name || '');
    }, [rawAttachments]);

    const mergeAttachment = (attachmentId: string, updatedData: Partial<Attachment>) => {
        setRawAttachments((attachments) =>
            attachments.map((attachment) => {
                if (attachment.attachmentId !== attachmentId) {
                    return attachment;
                }

                const fileName = generateUniqueFileName(
                    attachment.name || attachment.file_name || '',
                    exitingDocumentsNames.concat(rawAttachmentsFilesNames)
                );
                return merge(
                    {
                        ...attachment,
                    },
                    updatedData,
                    {
                        name: fileName,
                        file_name: fileName,
                    }
                );
            })
        );
    };

    const splitFileName = (filename: string) => {
        // Check if the input is valid
        if (typeof filename !== 'string' || !filename.includes('.')) {
            return { baseName: filename, extension: '' };
        }
        // Find the last occurrence of the dot
        const lastDotIndex = filename.lastIndexOf('.');
        // Split the base name and extension
        const baseName = filename.slice(0, lastDotIndex);
        const extension = filename.slice(lastDotIndex + 1);
        return { baseName, extension };
    };

    // returns fileName (counter+1) when file exists in the list
    const generateUniqueFileName = (fileName: string, existingFiles: string[]) => {
        if (!existingFiles.includes(fileName)) return fileName;

        // Extract file name and extension
        const { baseName, extension } = splitFileName(fileName);

        // Check if file name already exists
        let newFileName = fileName;
        let version = 1;

        // Loop until we find a unique file name
        while (existingFiles.includes(newFileName)) {
            newFileName = `${baseName} (${version++}).${extension}`;
        }

        return newFileName;
    };

    const onDropRejected = (fileRejections) => {
        const displayedErrorKey = fileRejections[0].errors.find((error) => UploadErrorCodesList.includes(error.code));
        const errorKey = displayedErrorKey?.code || UploadErrorCodes.default;
        setErrorMessage(translate(`${ERROR_TRANSLATIONS_PREFIX}.${errorKey?.replaceAll('-', '_')}`) as string);
    };

    const onDropAccepted: React.ComponentProps<typeof Dropzone>['onDropAccepted'] = (acceptedFiles) => {
        const id = generateAttachmentId();
        const attachments: Attachment[] = acceptedFiles.map((file) => ({
            ...file,
            id,
            size: file.size,
            type: file.type,
            name: generateUniqueFileName(file.name, exitingDocumentsNames.concat(rawAttachmentsFilesNames)),
            file_name: generateUniqueFileName(file.name, exitingDocumentsNames.concat(rawAttachmentsFilesNames)),
            attachmentId: id,
            uploaded: false,
            createdAt: Date.now(),
            progress: 0,
            src: URL.createObjectURL(file),
        }));

        setRawAttachments((existingAttachments) => [...existingAttachments, ...attachments]);

        acceptedFiles.forEach((file, index) => {
            const attachmentId = attachments[index].attachmentId;
            filerrUploader.upload({
                file,
                onProgress: ({ percentage }: DropzoneOnProgressData) =>
                    mergeAttachment(attachmentId, { progress: percentage }),
                onSuccess: ({ pendingAttachmentData }: DropzoneOnSuccessData) => {
                    const pendingAttachmentId =
                        pendingAttachmentData.attachment_id || pendingAttachmentData.pending_attachment_id;
                    mergeAttachment(attachmentId, {
                        ...pendingAttachmentData,
                        type: file.type,
                        file_type: file.type,
                        uploaded: true,
                        pending_file_id: pendingAttachmentId,
                        filerr_file_id: pendingAttachmentId,
                    });
                },
                onError: (error) => {
                    mergeAttachment(attachmentId, {
                        errorMessage: error?.message || 'Failed to upload file',
                    });

                    logger.error(error as Error);
                },
            });
        });
    };

    const removeAttachment = (attachmentId = '') => {
        setRawAttachments((attachments) =>
            attachments.filter((attachment) => attachment.attachmentId !== attachmentId)
        );
    };

    const onDrop = () => {
        setErrorMessage('');
    };

    return {
        rawAttachments,
        uploadProgress,
        onDropAccepted,
        onDropRejected,
        removeAttachment,
        uploadedFiles,
        resetState,
        onDrop,
        errorMessage,
    };
};
